/**
 * iOS Safari wallet banner.
 *
 * On a real iPhone, Phantom / Solflare on the App Store can't pop up from
 * a wallet-adapter call inside Safari the way a desktop extension can.
 * The accepted UX is a small banner that:
 *   1. Detects iPhone Safari (not Chrome-on-iOS, not iPad, not desktop).
 *   2. Offers an "Open in Phantom" universal-link CTA — tapping it deep
 *      links into the Phantom app with the current page URL, which then
 *      hands control back to Safari with a connected session.
 *   3. Hides once `useWallet().publicKey` is set, since the round-trip
 *      succeeded.
 *   4. Stays dismissible (sessionStorage) so a returning user isn't
 *      nagged.
 *
 * Detection + the universal-link target are both behind small swappable
 * helpers so the real-device test (banner copy, round-trip, post-connect
 * state) can plug in concrete logic without touching the layout. Wire-up
 * for the live link goes in `buildPhantomDeeplink` below — keep that the
 * single source of truth.
 *
 * Verified-on-device status: NOT YET. This component is wired up and
 * detection-correct in desktop devtools (User-Agent spoofing), but until
 * a real iPhone Safari round-trip is observed, treat the deeplink target
 * as a best-effort stub.
 */
import { useWallet } from "@solana/wallet-adapter-react";
import React, { useEffect, useMemo, useState } from "react";

const DISMISS_KEY = "solblox.iosWalletBanner.dismissed";

interface UA {
  isIphone: boolean;
  isSafari: boolean;
}

/**
 * iPhone Safari detection. Excludes iPad (UA reports "Macintosh" on
 * modern iPadOS, deliberately not handled — iPad wallet UX needs its
 * own pass) and excludes other iOS browsers that report CriOS / FxiOS /
 * EdgiOS in the UA.
 */
function detectUA(): UA {
  if (typeof navigator === "undefined") return { isIphone: false, isSafari: false };
  const ua = navigator.userAgent;
  const isIphone = /iPhone/.test(ua);
  const isSafari =
    /Safari\//.test(ua) &&
    !/CriOS|FxiOS|EdgiOS|OPiOS|GSA\//.test(ua) &&
    !/Chrome\//.test(ua);
  return { isIphone, isSafari };
}

/**
 * Real Phantom deeplinks require an encrypted payload (dapp pubkey, nonce,
 * redirect link). Stub returns Phantom's plain "browse" universal link
 * with the current URL as the target — enough to round-trip the user
 * back into Safari, even if it doesn't pre-connect. Swap this when the
 * real signing flow lands.
 */
function buildPhantomDeeplink(): string {
  if (typeof window === "undefined") return "https://phantom.app/ul/browse/";
  const here = encodeURIComponent(window.location.href);
  const ref = encodeURIComponent(window.location.origin);
  return `https://phantom.app/ul/browse/${here}?ref=${ref}`;
}

export function IosWalletDeeplinkBanner(): JSX.Element | null {
  const { publicKey } = useWallet();
  const ua = useMemo(detectUA, []);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof sessionStorage === "undefined") return false;
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  });

  useEffect(() => {
    if (!publicKey) return;
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.setItem(DISMISS_KEY, "1");
  }, [publicKey]);

  if (!ua.isIphone || !ua.isSafari) return null;
  if (publicKey) return null;
  if (dismissed) return null;

  const dismiss = (): void => {
    setDismissed(true);
    if (typeof sessionStorage !== "undefined") sessionStorage.setItem(DISMISS_KEY, "1");
  };

  return (
    <div style={bannerStyle} role="region" aria-label="Connect wallet on iPhone">
      <div style={copyStyle}>
        <div style={titleStyle}>Connect your wallet</div>
        <div style={subtitleStyle}>
          Safari can&apos;t open the Phantom popup on iPhone. Tap below to open
          this page inside the Phantom app, then come back.
        </div>
      </div>
      <div style={actionsStyle}>
        <a href={buildPhantomDeeplink()} style={primaryBtnStyle}>
          Open in Phantom
        </a>
        <button onClick={dismiss} style={dismissBtnStyle} aria-label="Dismiss banner">
          ×
        </button>
      </div>
    </div>
  );
}

const bannerStyle: React.CSSProperties = {
  position: "fixed",
  left: 8,
  right: 8,
  bottom: 8,
  padding: "10px 12px",
  background: "linear-gradient(135deg, rgba(28,36,51,0.97), rgba(8,8,13,0.97))",
  border: "1px solid #4a8eff",
  borderRadius: 10,
  color: "#e8e8ef",
  fontFamily:
    'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
  display: "flex",
  gap: 12,
  alignItems: "center",
  zIndex: 1200,
  boxShadow: "0 10px 30px rgba(0,0,0,0.55)",
};

const copyStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const titleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  marginBottom: 2,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#9aa0b4",
  lineHeight: 1.35,
};

const actionsStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  flex: "0 0 auto",
};

const primaryBtnStyle: React.CSSProperties = {
  display: "inline-block",
  padding: "8px 12px",
  background: "#4a8eff",
  color: "#0a0a12",
  fontSize: 12,
  fontWeight: 700,
  borderRadius: 6,
  textDecoration: "none",
  whiteSpace: "nowrap",
};

const dismissBtnStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  padding: 0,
  background: "transparent",
  color: "#9aa0b4",
  border: "1px solid #2a2a36",
  borderRadius: 6,
  fontSize: 16,
  lineHeight: 1,
  cursor: "pointer",
};
